<script setup lang="ts">
import axios from 'axios'
import { onMounted, ref } from 'vue'
import type { DraftLink, LandingData, LineItem } from '@/types'
import formatPrice from '@/helpers/formatPrice'
import { useRoute, useRouter } from 'vue-router'
// @ts-ignore
import { isProductionDomain } from '@/plugins/axios'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiHero from '@/components/UiHero/UiHero.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'

const landingData = ref<LandingData>()
const router = useRouter()
const route = useRoute()

function formatLinkPrice(link: DraftLink) {
  const currencyLabel = link.currency?.code === 'usd' ? 'USD' : 'руб'

  if (link.show_monthly_payment && link.monthly_payment) {
    return `от ${formatPrice(link.monthly_payment, true, currencyLabel)} / мес`
  }

  return formatPrice(link.price, true, currencyLabel)
}

async function getLandingData() {
  await axios.get('/d/links').then((response: { data: LandingData }) => {
    landingData.value = response.data
  })
}

onMounted(async () => {
  await getLandingData()
})

const selectLink = (id: string) => {
  if (isProductionDomain()) {
    router.push(`/d/${id}`)
  } else {
    const parther = route.params.parther
    router.push(`/${parther}/d/${id}`)
  }
}

const getOfferUrls = (items: LineItem[]) => {
  const offers: string[] = []

  if (!items) {
    return offers
  }

  for (const item of items) {
    if (item.offer_url) {
      offers.push(item.offer_url)
    } else if (item.legal_entity.offer) {
      offers.push(item.legal_entity.offer)
    }
  }

  return [...new Set(offers)]
}
</script>

<template>
  <div v-if="landingData" class="payment-cabinet">
    <div class="container">
      <ui-header :logo-src="landingData?.merchant?.logo_url ?? undefined"/>

      <ui-hero title="Официальная платежная страница" />

      <div class="UiCard" v-for="link in landingData.links">
        <div class="UiCard-wrapper">
          <div class="UiCard-info">
            <div class="UiCard-info__left">
              <div v-for="item in link.items">
                <div class="UiCard-info__title">
                  {{ item.name }}{{ link.items.length > 1 ? ` (${formatPrice(item.price, true, link.currency?.code === 'usd' ? 'USD' : 'руб')})` : '' }}
                </div>
                <div class="UiCard-info__description">
                  {{ item.description }}
                </div>
              </div>
            </div>

            <div class="UiCard-info__right">
              <div class="UiCard-info__price-info">
                <div class="UiCard-info__price-info-price">
                  {{ formatLinkPrice(link) }}
                </div>

                <button class="UiCard-info__button hoverable" @click="selectLink(link.id)">
                  <div class="UiCard-info__button-text">
                    <span class="UiCard-info__button-text__title">Оплатить</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="MerchantInfo">
          <h3>Контакты</h3>
          <div class="MerchantInfo__contacts">
            <p>{{ landingData.merchant.name }}</p>

            <p>
              <a :href="`tel:${landingData.merchant.phone}`">{{ landingData.merchant.phone }}</a>
            </p>

            <p>
              <a :href="`mailto:${landingData.merchant.email}`">{{ landingData.merchant.email }}</a>
            </p>
            <p>
              <a :href="landingData.merchant.url" target="_blank">{{ landingData.merchant.url }}</a>
            </p>
          </div>
        </div>

        <div class="MerchantInfo__legal">
          <div
            v-if="landingData.merchant.procedure_for_provision_of_services"
            class="MerchantInfo__legal-block"
          >
            <h3>Порядок оказания услуги</h3>
            <p class="markup" v-html="landingData.merchant.procedure_for_provision_of_services"></p>
          </div>
          <div v-if="landingData.merchant.warranty" class="MerchantInfo__legal-block">
            <h3>Гарантия</h3>
            <p class="markup" v-html="landingData.merchant.warranty"></p>
          </div>
          <div v-if="landingData.merchant.return" class="MerchantInfo__legal-block">
            <h3>Возврат</h3>
            <p class="markup" v-html="landingData.merchant.return"></p>
          </div>
        </div>
      </div>

      <ui-footer />
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/styles/variables';

.markup {
  ol,
  ul {
    list-style-type: auto;
  }

  strong {
    font-weight: bold;
  }
}

.MerchantInfo {
  margin-top: $font-size-base * 3;

  h3 {
    color: $c-dark;
    @include font-size($font-size-base * 1.2, 600, $font-size-base * 2.2);

    @media screen and (min-width: $breakpoint-md) {
      margin-bottom: $font-size-base;
      @include font-size($font-size-base * 1.8, 600, $font-size-base * 2.2);
    }
  }

  &__contacts {
    display: flex;
    flex-direction: column;
    gap: 0;

    @media screen and (min-width: $breakpoint-md) {
      gap: $font-size-base * 0.8;
    }

    p {
      color: $c-blue;
      @include font-size($font-size-base * 1, 500, $font-size-base * 2);

      @media screen and (min-width: $breakpoint-md) {
        @include font-size($font-size-base * 1.4, 500, $font-size-base * 2);
      }
      margin-bottom: 0;

      a {
        color: $c-blue;
        text-decoration: none;
      }
    }
  }

  &__legal {
    &-block {
      border-radius: 12px;

      padding: ($font-size-base * 4) ($font-size-base * 6);
      min-height: $font-size-base * 15;
      background: $c-blue-gradient;

      display: flex;
      flex-direction: column;

      margin-top: $font-size-base * 3;

      @include font-size($font-size-base * 1.2);

      h3 {
        margin-bottom: $font-size-base;

        color: $c-dark;
        @include font-size($font-size-base * 1.8, 600, $font-size-base * 2.2);
      }
    }
  }
}

.UiCard {
  padding-top: $font-size-base * 3;
  border-radius: 12px;

  position: relative;
  overflow: hidden;

  &-wrapper {
    padding: ($font-size-base * 2) ($font-size-base * 2);
    background: $c-blue-gradient;
    display: flex;
    justify-content: space-between;

    border-radius: inherit;
  }

  &-info {
    display: flex;
    position: relative;
    gap: 4px;
    justify-content: space-between;
    width: 100%;

    &__left {
      display: flex;
      flex-direction: column;
      flex: 2;

      order: 1;
    }

    &__right {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex: 1;
      order: 2;
    }

    &__price-info {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;

      &-price {
        display: block;
        margin-right: $font-size-base * 2;
        white-space: nowrap;
        color: $c-dark;
        @include font-size(16px, 600, $font-size-base * 2.2);

        @media screen and (min-width: 768px) {
          @include font-size($font-size-base * 1.2, 600, $font-size-base * 2.2);
        }
      }
    }

    &__title {
      word-wrap: anywhere;
      display: block;
      margin-bottom: $font-size-base;
      color: $c-dark;
      @include font-size($font-size-base * 1.2, 600, $font-size-base * 2.2);
    }

    &__description {
      display: block;
      margin-bottom: $font-size-base * 1.4;
      color: $c-blue;
      @include font-size($font-size-base * 0.9, 500, $font-size-base * 1.2);

      @media screen and (min-width: 768px) {
        @include font-size($font-size-base * 1, 500, $font-size-base * 1.5);
      }
    }

    &__offers {
      display: inline-flex;
      justify-content: space-between;
    }

    &__offer {
      display: block;

      margin-top: 0;
      @include font-size($font-size-base);

      margin-right: 1rem;

      &:last-child {
        margin-right: 0;
      }

      a {
        color: $c-dark;
      }
    }

    &__button {
      background: $c-light;
      border-radius: 12px;
      border: 0;
      padding: 12px 12px;
      position: relative;

      &-text {
        position: relative;

        &__title {
          display: block;
          text-align: center;

          color: $c-dark;
          @include font-size($font-size-base * 1.1, 400);
          @media screen and (min-width: $breakpoint-md) {
            @include font-size($font-size-base * 1.2, 400);
          }
        }
      }
    }
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .UiCard {
    padding-top: 0;
    overflow: hidden;
    margin-top: $font-size-base * 2;

    &-wrapper {
      min-height: auto;
    }

    &-info {
      &__price-info {
        flex-direction: column;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .UiCard {
    &-wrapper {
      flex-direction: column;
      padding: $font-size-base * 2;
    }

    &-info {
      &__title {
        margin-bottom: $font-size-base * 1.2;
        @include font-size($font-size-base * 1.3, 600, $font-size-base * 1.35);
      }

      &__price {
        margin-top: 0;
        @include font-size($font-size-base * 2.5, 700, $font-size-base * 2);
      }

      &__offer {
        margin-top: $font-size-base * 0.5;

        @include font-size($font-size-base * 0.5, 500, $font-size-base * 0.7);
      }
    }
  }

}

@media screen and (max-width: $breakpoint-xs) {
  .UiCard {
    &-wrapper {
      padding: $font-size-base * 1.2;
    }

    &-info {
      flex-direction: column;

      &__price-info {
        flex-direction: row;
      }
    }
  }
}

@media screen and (min-width: $breakpoint-md) {
  .hoverable {
    overflow: hidden;
    cursor: pointer;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: $c-dark;
      pointer-events: none;
      user-select: none;
      outline: none;
      opacity: 0;
      transition-duration: inherit;
      border-radius: inherit;
    }

    &:hover {
      box-shadow: 0 0 0 $c-white;

      &:before {
        opacity: 1;
      }

      & .UiCard-info__button-text,
      & .UiCard-info__button-text__title {
        color: $c-white;
      }

      & svg {
        fill: $c-white;
      }
    }
  }
}
</style>
